// services/captureOrchestrator.js
// Capture hierarchy: Custom (Capacitor/getUserMedia) → Native camera → Backup upload (exception).
// Original IP — LoadDash Services.

const LOG = (event, meta = {}) => console.log(`[CAPTURE] ${event}`, meta);

export const CAPTURE_ERRORS = {
  camera_permission_denied:   "Camera access is off. Enable camera permission in your device settings.",
  native_camera_unavailable:  "We couldn't open the device camera.",
  web_camera_unavailable:     "Live camera isn't available here.",
  file_input_no_selection:    "No image was selected. Tap to try again.",
  secure_context_required:    "Camera requires a secure connection.",
  no_capture_path_available:  "No camera path is available on this device. Please try from a different browser or device.",
  user_cancelled:             "Cancelled.",
  native_camera_failed:       "Device camera could not capture. You can use backup upload.",
};

export function getCameraCapability() {
  const isNativeApp = !!(window.Capacitor?.isNativePlatform?.());
  return {
    isNativeApp,
    hasCapacitorCamera: isNativeApp,
    hasMediaDevices: !!(window.isSecureContext && navigator.mediaDevices?.getUserMedia),
    isSecureContext: !!window.isSecureContext,
    hasFileCaptureFallback: true,
  };
}

/**
 * Capture a photo for the given slot.
 * Ladder: Capacitor → getUserMedia → native camera → backup upload (exception).
 * Cancel at any stage stops the chain — does not fall through.
 */
export async function capturePhoto(slot, options = {}) {
  const capability = getCameraCapability();
  LOG("capture_started", { slot, capability });

  // ── Step 1: Capacitor Camera — DISABLED ──
  // Skipped to force getUserMedia on all platforms so LensIQ overlay renders.
  // Capacitor Camera launches the OS camera activity which cannot host custom UI.
  // To re-enable: restore the hasCapacitorCamera condition below.
  // if (capability.hasCapacitorCamera) {
  //   LOG("capture_method_attempted", { method: "custom_capacitor", slot });
  //   const result = await attemptCapacitorCamera(slot);
  //   if (result?._cancelled) return { ok: false, errorCode: "user_cancelled", errorMessage: CAPTURE_ERRORS.user_cancelled };
  //   if (result) return result;
  // }

  // ── Step 2: getUserMedia (custom browser capture) ──
  if (capability.hasMediaDevices) {
    LOG("capture_method_attempted", { method: "custom_browser", slot });
    const result = await attemptGetUserMedia(slot, options.showMediaModal);
    if (result?._cancelled) return { ok: false, errorCode: "user_cancelled", errorMessage: CAPTURE_ERRORS.user_cancelled };
    if (result) return result;
    // Technical failure → continue to native camera
  } else if (!capability.isSecureContext) {
    LOG("capture_failed", { errorCode: "secure_context_required", slot });
  }

  // ── Step 3: Native device camera (file input with capture="environment") ──
  LOG("capture_method_attempted", { method: "native_camera", slot });
  const nativeResult = await attemptNativeCamera(slot);
  if (nativeResult) return nativeResult;

  // ── Step 4: Backup upload (exception only — no capture attribute) ──
  LOG("capture_method_attempted", { method: "backup_upload_exception", slot });
  const uploadResult = await attemptBackupUpload(slot);
  if (uploadResult) return uploadResult;

  // All failed
  LOG("capture_failed", { errorCode: "no_capture_path_available", slot });
  return { ok: false, errorCode: "no_capture_path_available", errorMessage: CAPTURE_ERRORS.no_capture_path_available };
}

// ── Capacitor Camera ──────────────────────────────

async function attemptCapacitorCamera(slot) {
  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    LOG("permission_requested", { method: "capacitor_camera", slot });
    const perms = await Camera.checkPermissions();
    LOG("permission_result", { camera: perms.camera, photos: perms.photos });

    if (perms.camera === "denied") {
      LOG("capture_failed", { errorCode: "camera_permission_denied", slot });
      return { ok: false, errorCode: "camera_permission_denied", errorMessage: CAPTURE_ERRORS.camera_permission_denied };
    }
    if (perms.camera === "prompt" || perms.camera === "prompt-with-rationale") {
      const req = await Camera.requestPermissions({ permissions: ["camera"] });
      LOG("permission_result", { camera: req.camera, afterRequest: true });
      if (req.camera === "denied") {
        return { ok: false, errorCode: "camera_permission_denied", errorMessage: CAPTURE_ERRORS.camera_permission_denied };
      }
    }

    try {
      const image = await Camera.getPhoto({ quality: 85, allowEditing: false, resultType: CameraResultType.Uri, source: CameraSource.Camera, saveToGallery: false });
      const response = await fetch(image.webPath || image.path);
      const blob = await response.blob();
      const file = new File([blob], `capture_slot${slot}_${Date.now()}.jpg`, { type: "image/jpeg" });
      const previewUrl = URL.createObjectURL(file);
      LOG("capture_returned", { ok: true, method: "capacitor_camera", slot });
      return { ok: true, slot, file, previewUrl, source: "capacitor_camera", capturedAt: new Date().toISOString() };
    } catch (captureErr) {
      const msg = (captureErr?.message || String(captureErr)).toLowerCase();
      if (msg.includes("cancel")) {
        LOG("capture_returned", { ok: false, method: "capacitor_camera", reason: "user_cancelled", slot });
        return { _cancelled: true };
      }
      LOG("capture_failed", { errorCode: "native_camera_unavailable", error: msg, slot });
      return null;
    }
  } catch (importErr) {
    LOG("capture_failed", { errorCode: "native_camera_unavailable", error: importErr?.message, slot });
    return null;
  }
}

// ── getUserMedia (custom browser capture) ─────────

async function attemptGetUserMedia(slot, showModal) {
  let stream = null;
  try {
    LOG("permission_requested", { method: "get_user_media", slot });
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
    LOG("permission_result", { granted: true, method: "get_user_media" });

    if (showModal) {
      const captureResult = await new Promise((resolve) => {
        showModal({
          stream, slot,
          onCapture: (blob) => { stopAllTracks(stream); resolve(blob); },
          onCancel: () => { stopAllTracks(stream); resolve("_cancelled"); },
        });
      });
      if (captureResult === "_cancelled") {
        LOG("capture_returned", { ok: false, method: "get_user_media", reason: "user_cancelled", slot });
        return { _cancelled: true };
      }
      if (!captureResult) return null;
      const file = new File([captureResult], `capture_slot${slot}_${Date.now()}.jpg`, { type: "image/jpeg" });
      const previewUrl = URL.createObjectURL(file);
      LOG("capture_returned", { ok: true, method: "get_user_media", slot });
      return { ok: true, slot, file, previewUrl, source: "get_user_media", capturedAt: new Date().toISOString() };
    }

    // No modal — auto-capture
    const video = document.createElement("video");
    video.srcObject = stream;
    video.setAttribute("autoplay", ""); video.setAttribute("playsinline", ""); video.setAttribute("muted", "");
    await video.play();
    await new Promise(r => setTimeout(r, 500));
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    stopAllTracks(stream); stream = null;
    const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", 0.85));
    if (!blob) { LOG("capture_failed", { errorCode: "web_camera_unavailable", reason: "canvas_toBlob_failed", slot }); return null; }
    const file = new File([blob], `capture_slot${slot}_${Date.now()}.jpg`, { type: "image/jpeg" });
    const previewUrl = URL.createObjectURL(file);
    LOG("capture_returned", { ok: true, method: "get_user_media", slot });
    return { ok: true, slot, file, previewUrl, source: "get_user_media", capturedAt: new Date().toISOString() };
  } catch (err) {
    if (stream) stopAllTracks(stream);
    const msg = err?.message || String(err);
    if (msg.includes("Permission") || msg.includes("NotAllowed")) {
      LOG("capture_failed", { errorCode: "camera_permission_denied", error: msg, slot });
    } else {
      LOG("capture_failed", { errorCode: "web_camera_unavailable", error: msg, slot });
    }
    return null;
  }
}

function stopAllTracks(stream) {
  if (!stream) return;
  try { stream.getTracks().forEach(t => t.stop()); } catch {}
}

// ── Native device camera (file input with capture="environment") ──

function attemptNativeCamera(slot) {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.setAttribute("capture", "environment"); // forces camera, not gallery
    input.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
    document.body.appendChild(input);
    let resolved = false;
    const finish = (result) => { if (resolved) return; resolved = true; try { document.body.removeChild(input); } catch {} resolve(result); };

    input.addEventListener("change", (e) => {
      const f = e.target.files?.[0];
      if (!f) { LOG("capture_failed", { errorCode: "native_camera_failed", slot }); finish(null); return; }
      const previewUrl = URL.createObjectURL(f);
      LOG("capture_returned", { ok: true, method: "native_camera", slot });
      finish({ ok: true, slot, file: f, previewUrl, source: "native_camera", capturedAt: new Date().toISOString() });
    });

    const onFocus = () => {
      setTimeout(() => {
        if (!resolved && !input.files?.length) { LOG("capture_failed", { errorCode: "native_camera_failed", slot }); finish(null); }
        window.removeEventListener("focus", onFocus);
      }, 2000);
    };
    window.addEventListener("focus", onFocus);
    input.click();
  });
}

// ── Backup upload (exception only — no capture attribute) ──

function attemptBackupUpload(slot) {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    // NO capture attribute — opens gallery/file picker, not camera
    input.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
    document.body.appendChild(input);
    let resolved = false;
    const finish = (result) => { if (resolved) return; resolved = true; try { document.body.removeChild(input); } catch {} resolve(result); };

    input.addEventListener("change", (e) => {
      const f = e.target.files?.[0];
      if (!f) { LOG("capture_failed", { errorCode: "file_input_no_selection", slot }); finish(null); return; }
      const previewUrl = URL.createObjectURL(f);
      LOG("capture_returned", { ok: true, method: "backup_upload_exception", slot });
      finish({ ok: true, slot, file: f, previewUrl, source: "backup_upload", capturedAt: new Date().toISOString() });
    });

    const onFocus = () => {
      setTimeout(() => {
        if (!resolved && !input.files?.length) { LOG("capture_failed", { errorCode: "file_input_no_selection", slot }); finish(null); }
        window.removeEventListener("focus", onFocus);
      }, 2000);
    };
    window.addEventListener("focus", onFocus);
    input.click();
  });
}
